import React from 'react';

/**
 * Top-level React error boundary.
 *
 * Without this, any unhandled render-phase exception (e.g. dereferencing a null
 * timestamp inside Notifications) unmounts the entire React tree and leaves a
 * blank white screen — which is exactly what the iOS / TestFlight Notifications
 * "New" filter regression was producing.
 *
 * With this in place, a render crash inside any route is contained: the user
 * sees a recoverable error UI with a "Go to Dashboard" button instead of an
 * unrecoverable white screen, and the full stack trace is logged to the
 * native console for Capacitor / TestFlight debugging.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log so it surfaces in Capacitor / Safari Web Inspector console.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render crash:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    // Use a hard navigation so we drop any corrupted router state.
    window.location.assign('/dashboard');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 text-center">
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-zinc-600 mb-6">
            This screen hit an unexpected error. You can head back to your dashboard or try this screen again.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleGoHome}
              className="px-4 py-2 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Go to Dashboard
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 rounded-lg border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
              data-testid="error-boundary-retry-btn"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
